interface OpenResponseDeps {
  buildHeaders: (includeContentType: boolean, extra: Record<string, string>) => Record<string, string>;
  addCsrfHeader: (method: string, headers: Record<string, string>) => Record<string, string>;
  execute: (endpoint: string, init: RequestInit) => Promise<Response>;
  syncCsrf: (response: Response) => void;
  refresh: () => Promise<boolean>;
  clearSession: () => void;
  rehydrateCsrf: () => Promise<boolean>;
  isCsrfFailure: (status: number, data: Record<string, unknown>) => boolean;
  makeError: (body: unknown, status: number) => Error;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function errorMessage(data: Record<string, unknown> | null): string {
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data?.error === 'string' && data.error) return data.error;
  return 'API request failed';
}

export async function openAuthenticatedResponse(
  deps: OpenResponseDeps,
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<Response> {
  const hasBody = options.body !== undefined && options.body !== null;
  const method = (options.method ?? 'GET').toUpperCase();
  const extra = (options.headers as Record<string, string> | undefined) ?? {};
  const headers = deps.addCsrfHeader(method, deps.buildHeaders(hasBody, extra));
  const response = await deps.execute(endpoint, { credentials: options.credentials ?? 'include', ...options, headers });
  deps.syncCsrf(response);
  if (response.status === 401 && !isRetry) {
    if (await deps.refresh()) return openAuthenticatedResponse(deps, endpoint, options, true);
    deps.clearSession();
    throw deps.makeError({ error: 'Unauthorized' }, 401);
  }
  if (response.status === 403 && !isRetry) {
    const data = record(await response.clone().json().catch((): unknown => null));
    if (data && deps.isCsrfFailure(response.status, data)
      && (await deps.rehydrateCsrf() || await deps.refresh())) {
      return openAuthenticatedResponse(deps, endpoint, options, true);
    }
  }
  if (!response.ok) {
    const data = record(await response.clone().json().catch((): unknown => null));
    throw deps.makeError(data ?? { error: errorMessage(data) }, response.status);
  }
  return response;
}
